import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useListPricingPlanTickRates } from 'src/features/pricingPlan/api/tickRate/useListPricingPlanTickRates';
import CreateTickRateFormModal from 'src/features/pricingPlan/components/form/tickRate/CreateTickRateFormModal';
import CellTickRatesActions from 'src/features/pricingPlan/components/table/CellTickRatesActions';
import { PricingPlanTickRate } from 'src/features/pricingPlan/utils/types';
import { DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/pricingPlan/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSorting from 'src/registry/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

const PricingPlanRatesTable = () => {
    const { formatMessage, formatNumber } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();

    const managePricingPlanTickRates = useHasPermissions([PERMISSION.MANAGE_PRICING_PLAN_TICK_RATES]);

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'day_of_week', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const {
        data: pricingPlanTickRatesData,
        isLoading,
        isError,
    } = useListPricingPlanTickRates({
        planId,
        parkingId,
        ...queryFiltersOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
        config: {
            enabled: !!planId && !!parkingId,
        },
    });

    const columnHelper = createColumnHelper<PricingPlanTickRate>();

    const columns = [
        columnHelper.accessor('day_of_week', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: ({ row }) => formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[row.original.day_of_week]),
            meta: {
                title: formatMessage(pricingPlanMessages.day),
                inputType: INPUT_TYPE.MULTI_SELECT,
                options: Object.entries(DAY_OF_WEEK_TRANSLATIONS_MAP).map(([key, value]) => ({
                    label: formatMessage(value),
                    value: key,
                })),
            },
        }),
        columnHelper.accessor('from_time', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({ row }) => {
                return (
                    <div className="flex flex-row">
                        <div>{`${row.original.from_time} - ${row.original.to_time}`}</div>
                    </div>
                );
            },
            meta: {
                title: formatMessage(pricingPlanMessages.hours),
            },
        }),
        columnHelper.accessor('rate_with_tax', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({ row }) => {
                const value = row.original.rate_with_tax;
                return formatNumber(value / 100, {
                    style: 'currency',
                    currency: row.original.currency,
                });
            },
            meta: {
                title: formatMessage(pricingPlanMessages.rate),
            },
        }),
        ...(managePricingPlanTickRates
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellTickRatesActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<PricingPlanTickRate, any>
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={pricingPlanTickRatesData}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            enableSearchParams={false}
            semantics={{
                title: formatMessage(pricingPlanMessages.rates),
                entryName: formatMessage(pricingPlanMessages.rate).toLowerCase(),
                entryNames: formatMessage(pricingPlanMessages.rates).toLowerCase(),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(managePricingPlanTickRates && { newEntry: CreateTickRateFormModal }),
            }}
        />
    );
};

export default PricingPlanRatesTable;
